import React from 'react';
import {
  Box,
  Stack,
  Typography,
} from '@mui/material';
import { LinkExternal, CustomIcons } from '@marageti/z4-lib';
import { ReactComponent as ZicassoSVG } from '../../assets/svg/zicasso-logo-redesign-white.svg';
import config from '../../config';

const {
  TwitterIcon,
  FacebookIcon,
  InstagramIcon,
} = CustomIcons;

const companyLinks = [
  {
    title: 'About Our Services',
    link: `${config.Z3_URL}about-us`,
    external: true,
    ariaLabel: 'Link to our About page',
  },
  {
    title: 'Zicasso in the News',
    link: `${config.Z3_URL}news`,
    external: true,
    ariaLabel: 'Zicasso in the News',
  },
  {
    title: 'Travel Insurance',
    link: `${config.Z3_URL}travel-insurance/`,
    external: true,
    ariaLabel: 'Link to our Travel Insurance page',
  },
  {
    title: 'Travel Specialist Partnerships',
    link: `${config.Z3_URL}contact-travel-company`,
    external: true,
    ariaLabel: 'Travel Specialist Partnerships',
  },
  {
    title: 'Careers',
    link: `${config.Z3_URL}jobs`,
    external: true,
    ariaLabel: 'Link to our Careers page',
  },
  {
    title: 'Contact Us',
    link: `${config.Z3_URL}contact`,
    external: true,
    ariaLabel: 'Link to our Contact page',
  },
];

const popularDestinationsLinks = [
  {
    title: 'Italy',
    link: `${config.Z3_URL}luxury-tours/italy/vacation-package-travel-agency/`,
    external: true,
    ariaLabel: 'Italy',
  },
  {
    title: 'Greece',
    link: `${config.Z3_URL}luxury-tours/greece-vacations/`,
    external: true,
    ariaLabel: 'Greece',
  },
  {
    title: 'Australia',
    link: `${config.Z3_URL}luxury-tours/australia/vacation-package-travel-agency/`,
    external: true,
    ariaLabel: 'Australia',
  },
  {
    title: 'Spain',
    link: `${config.Z3_URL}luxury-tours/spain/vacation-package-travel-agency/`,
    external: true,
    ariaLabel: 'Spain',
  },
  {
    title: 'Japan',
    link: `${config.Z3_URL}luxury-tours/japan/vacation-package-travel-agency/`,
    external: true,
    ariaLabel: 'Japan',
  },
  {
    title: 'African Safaris',
    link: `${config.Z3_URL}african-safari/`,
    external: true,
    ariaLabel: 'African Safaris',
  },
];

const Services = () => (
  <Stack
    data-e2e="ztest-footer-company"
    direction="column"
    spacing={1.5}
    width={{ desktop: '255px', mobile: '100%' }}
  >
    <Typography color="primary.contrastText" variant="h4">
      Company
    </Typography>
    {companyLinks.map((link) => (
      <LinkExternal
        ariaLabel={link.ariaLabel}
        color="grey.A100"
        href={link.link}
        key={link.title}
        variant="caption"
        sx={{ '&:hover': { color: { desktop: 'secondary.main' } } }}
      >
        {link.title}
      </LinkExternal>
    ))}
  </Stack>
);

const Destinations = () => (
  <Stack
    data-e2e="ztest-footer-destinations"
    direction="column"
    spacing={1.5}
    width={{ desktop: '255px', mobile: '100%' }}
  >
    <Typography color="primary.contrastText" variant="h4">
      Popular Destinations
    </Typography>
    {popularDestinationsLinks.map((link) => (
      <LinkExternal
        ariaLabel={link.ariaLabel}
        color="grey.A100"
        href={link.link}
        key={link.title}
        variant="caption"
        sx={{ '&:hover': { color: { desktop: 'secondary.main' } } }}
      >
        {link.title}
      </LinkExternal>
    ))}
  </Stack>
);

const SocialMedia = () => (
  <Stack
    data-e2e="ztest-footer-social"
    direction="column"
    spacing={2}
    width={{ desktop: '255px', mobile: '100%' }}
  >
    <Typography color="grey.A100" variant="subtitle1">
      650 Castro St., Suite 120-447, Mountain
      <br />
      View, CA 94041
    </Typography>
    <LinkExternal
      ariaLabel="Link to our Twitter page"
      color="grey.A100"
      href="https://twitter.com/zicassotravel"
      key="twitter-logo"
      variant="caption"
      sx={{
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          color: 'secondary.main',
          '& .MuiSvgIcon-root > path': {
            fill: 'secondary.main',
          },
        },
      }}
    >
      <TwitterIcon sx={{ marginRight: 1, '> path': { fill: 'inherit' } }} />
      Twitter
    </LinkExternal>
    <LinkExternal
      ariaLabel="Link to our Instagram page"
      color="grey.A100"
      href="https://www.instagram.com/zicassotravel/?hl=en"
      key="Instagram"
      variant="caption"
      sx={{
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          color: 'secondary.main',
          '& .MuiSvgIcon-root > path': {
            fill: 'secondary.main',
          },
        },
      }}
    >
      <InstagramIcon sx={{ marginRight: 1, '> path': { fill: 'inherit' } }} />
      Instagram
    </LinkExternal>
    <LinkExternal
      ariaLabel="Link to our Facebook page"
      color="grey.A100"
      href="https://www.facebook.com/Zicasso/"
      key="Facebook"
      variant="caption"
      sx={{
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
          color: 'secondary.main',
          '& .MuiSvgIcon-root > path': {
            fill: 'secondary.main',
          },
        },
      }}
    >
      <FacebookIcon sx={{ marginRight: 1, '> path': { fill: 'inherit' } }} />
      Facebook
    </LinkExternal>
  </Stack>
);

const LegalNotices = () => (
  <Stack
    direction="column"
    spacing={2}
    width={{ desktop: '255px', mobile: '100%' }}
  >
    <Typography color="grey.A100" variant="subtitle1">
      Copyright ©
      {' '}
      {new Date().getFullYear()}
      {' '}
      Zicasso, Inc. All Rights
      <br />
      Reserved. CST 2124858-50.
    </Typography>
    <Typography color="grey.A100" variant="subtitle1">
      <LinkExternal
        ariaLabel="Link to Zicasso's Terms of Use"
        color="grey.A100"
        href={`${config.Z3_URL}zicasso-terms-use`}
        key="Zicasso's Terms of Use"
        variant="caption"
        sx={{ '&:hover': { color: { desktop: 'secondary.main' } } }}
      >
        Terms of Use
      </LinkExternal>
      &nbsp;|&nbsp;
      <LinkExternal
        ariaLabel="Link to Zicasso's Privacy Policy"
        color="grey.A100"
        href={`${config.Z3_URL}zicasso-privacy-policy`}
        key="Zicasso's Privacy Policy"
        variant="caption"
        sx={{ '&:hover': { color: { desktop: 'secondary.main' } } }}
      >
        Privacy Policy
      </LinkExternal>
    </Typography>
  </Stack>
);

const FooterView = () => (
  <Box
    component="footer"
    sx={{
      bgcolor: 'primary.main',
    }}
  >
    <Stack
      direction={{ desktop: 'row', mobile: 'column' }}
      justifyContent="space-between"
      padding={{ desktop: '80px', mobile: '56px 24px' }}
      margin={{ desktop: '0 auto', mobile: '0' }}
      spacing={{ desktop: 0, mobile: 4 }}
      width="100%"
      maxWidth="1728px"
    >
      <Stack
        direction={{ desktop: 'row', mobile: 'column' }}
        spacing={{ desktop: 2.625, mobile: 4 }}
        width="100%"
      >
        <Services />
        <Destinations />
      </Stack>
      <Stack
        direction="column"
        spacing={1}
        width="100%"
        maxWidth="531px"
      >
        <ZicassoSVG />
        <Stack
          direction={{ desktop: 'row', mobile: 'column' }}
          spacing={2.625}
          width="100%"
        >
          <SocialMedia />
          <LegalNotices />
        </Stack>
      </Stack>
    </Stack>
  </Box>
);

export default FooterView;
